/**
 * The animals.
 *
 * Kate's central rule: the animal IS the weapon. "It's like if you're a bear, you have
 * an amount of strength... if you're a wolf, you have amount of strength and endurance
 * and speed." Asked whether players carry weapons, she said no — only the human buys
 * them, and only with coupons.
 *
 * Every stat and ability below traces to something she said. Where she left a species
 * blank (the deer), it is marked and queued for her rather than invented.
 */
import type { BiomeId } from '../world/biomes'

export type SpeciesId =
  | 'human'
  | 'bear'
  | 'wolf'
  | 'bunny'
  | 'bird'
  | 'cat'
  | 'dog'
  | 'chameleon'
  | 'deer'
  | 'camel'
  | 'dolphin'
  | 'whale'
  | 'fish'

export type Ability =
  | 'burrow'      // every animal, per Kate
  | 'sniff'       // wolf: crouch to sniff, green trail to prey
  | 'camouflage'  // chameleon / lizard
  | 'fly'         // bird, capped at tree height
  | 'climb'       // cat and dog; the cat's counter to the bird
  | 'killBirds'   // cat specifically — "you can climb trees and kill the bird"
  | 'nest'        // bird: "they have nests in the trees"
  | 'buyWeapons'  // human only
  | 'bePet'       // dog and cat: "humans can take care of dogs and cats"
  | 'swim'        // ocean species

export interface Species {
  id: SpeciesId
  name: string
  /** Damage dealt per second of a held attack. The animal is the weapon. */
  strength: number
  /** How long it can sprint. Kate named endurance as a per-species stat. */
  endurance: number
  /** Metres per second walking. */
  speed: number
  /** Jump launch speed — the bunny "can hop really high". */
  jump: number
  abilities: readonly Ability[]
  /**
   * Coupons to morph into it. Kate gave two prices in two places: "every game you
   * start with 50, so you can only buy one thing" makes an ordinary morph 50, and
   * "they all cost... twenty-five coupons" was said about the desert and ocean
   * animals specifically. So: 50 normally, 25 for the new-biome species.
   */
  cost: number
  /** Restricted to a biome, or available everywhere. */
  biome: BiomeId | 'any'
  /** Body colour until real models exist. */
  color: number
  /** Rough body size, used for the placeholder mesh and collision feel. */
  scale: number
  /** Noted where Kate never specified the animal's power. */
  unspecified?: true
}

/** Baseline the human is measured against; other species scale from here. */
const HUMAN_SPEED = 4.5
const HUMAN_JUMP = 7.0

export const SPECIES: Record<SpeciesId, Species> = {
  human: {
    id: 'human', name: 'Human',
    // The human has no natural weapon — Kate gave it purchased ones instead.
    strength: 1, endurance: 1, speed: HUMAN_SPEED, jump: HUMAN_JUMP,
    abilities: ['burrow', 'buyWeapons'],
    cost: 0, biome: 'any', color: 0xb98d6f, scale: 1,
  },
  bear: {
    id: 'bear', name: 'Bear',
    // "If you're a bear, it has strong claws." The heaviest hitter, and slow.
    strength: 5, endurance: 3, speed: HUMAN_SPEED * 0.95, jump: HUMAN_JUMP * 0.85,
    abilities: ['burrow'],
    cost: 50, biome: 'any', color: 0x4a3627, scale: 1.6,
  },
  wolf: {
    id: 'wolf', name: 'Wolf',
    // "Strong teeth", plus strength AND endurance AND speed — she listed all three.
    strength: 4, endurance: 5, speed: HUMAN_SPEED * 1.35, jump: HUMAN_JUMP * 0.95,
    abilities: ['burrow', 'sniff'],
    cost: 50, biome: 'any', color: 0x5f5a52, scale: 1.1,
  },
  bunny: {
    id: 'bunny', name: 'Bunny',
    // "The bunny's weapon is their speed... really fast and can hop really high."
    strength: 1, endurance: 2, speed: HUMAN_SPEED * 1.75, jump: HUMAN_JUMP * 1.75,
    abilities: ['burrow'],
    cost: 50, biome: 'any', color: 0xa79684, scale: 0.5,
  },
  bird: {
    id: 'bird', name: 'Bird',
    // Flies, but only to tree height — and the cat is its counter.
    strength: 1, endurance: 4, speed: HUMAN_SPEED * 1.2, jump: HUMAN_JUMP,
    abilities: ['burrow', 'fly', 'nest'],
    cost: 50, biome: 'any', color: 0x3c5a86, scale: 0.4,
  },
  cat: {
    id: 'cat', name: 'Cat',
    // "What if you're a cat? You can climb trees and kill the bird."
    strength: 2, endurance: 3, speed: HUMAN_SPEED * 1.3, jump: HUMAN_JUMP * 1.3,
    abilities: ['burrow', 'climb', 'killBirds', 'bePet'],
    cost: 50, biome: 'any', color: 0x8a7b63, scale: 0.55,
  },
  dog: {
    id: 'dog', name: 'Dog',
    abilities: ['burrow', 'climb', 'bePet'],
    strength: 3, endurance: 4, speed: HUMAN_SPEED * 1.25, jump: HUMAN_JUMP * 1.1,
    cost: 50, biome: 'any', color: 0x7a5c3a, scale: 0.7,
  },
  chameleon: {
    id: 'chameleon', name: 'Chameleon',
    // "There's like lizards, chameleons. You can camouflage." Her pick for hide-and-seek.
    strength: 1, endurance: 2, speed: HUMAN_SPEED * 0.8, jump: HUMAN_JUMP * 0.8,
    abilities: ['burrow', 'camouflage', 'climb'],
    cost: 50, biome: 'any', color: 0x4e7a3a, scale: 0.35,
  },
  deer: {
    id: 'deer', name: 'Deer',
    // Kate listed the deer but never gave it a power. Speed is the smallest reading of
    // a deer consistent with the rest; see Questions for Kate.
    strength: 2, endurance: 5, speed: HUMAN_SPEED * 1.45, jump: HUMAN_JUMP * 1.2,
    abilities: ['burrow'],
    cost: 50, biome: 'any', color: 0x8a6a45, scale: 1.2,
    unspecified: true,
  },
  camel: {
    id: 'camel', name: 'Camel',
    // "In the desert, there's camels, and that's it. Just camels is new."
    strength: 3, endurance: 6, speed: HUMAN_SPEED * 1.1, jump: HUMAN_JUMP * 0.8,
    abilities: ['burrow'],
    cost: 25, biome: 'desert', color: 0xc2a06a, scale: 1.5,
  },
  dolphin: {
    id: 'dolphin', name: 'Dolphin',
    strength: 3, endurance: 5, speed: HUMAN_SPEED * 1.6, jump: HUMAN_JUMP * 1.3,
    abilities: ['swim'],
    cost: 25, biome: 'ocean', color: 0x6f8ea8, scale: 1.1,
  },
  whale: {
    id: 'whale', name: 'Whale',
    strength: 6, endurance: 6, speed: HUMAN_SPEED * 0.85, jump: HUMAN_JUMP * 0.5,
    abilities: ['swim'],
    cost: 25, biome: 'ocean', color: 0x415a70, scale: 2.6,
  },
  fish: {
    id: 'fish', name: 'Fish',
    strength: 1, endurance: 3, speed: HUMAN_SPEED * 1.15, jump: HUMAN_JUMP * 0.6,
    abilities: ['swim'],
    cost: 25, biome: 'ocean', color: 0x9aa86f, scale: 0.3,
  },
}

export const SPECIES_ORDER: SpeciesId[] = [
  'human', 'bear', 'wolf', 'bunny', 'bird', 'cat', 'dog', 'chameleon', 'deer',
  'camel', 'dolphin', 'whale', 'fish',
]

export function has(species: Species, ability: Ability): boolean {
  return species.abilities.includes(ability)
}

/** Which species can be morphed into in a given biome. */
export function availableIn(biome: BiomeId): Species[] {
  return SPECIES_ORDER.map((id) => SPECIES[id]).filter(
    (s) => s.biome === 'any' || s.biome === biome,
  )
}
